import React, { useState, useEffect } from 'react';
import { Button } from "src/components/ui/button";
import { Card } from "src/components/ui/card";
import { Bell, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useNotifications } from '../contexts/NotificationContext';
import LoadingBar from '../components/ui/LoadingBar';
import './NotificationsPage.css';

// Define a notification type based on the actual interface in NotificationService.ts
interface Notification {
  id: number;
  type: string;
  message: string;
  link: string | null;
  created_at: string;
  read: boolean;
}

const NotificationsPage: React.FC = () => {
  const { notifications, markAsRead, markAllAsRead } = useNotifications();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleMarkAsRead = (id: number) => {
    markAsRead(id);
  };
  
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };
  
  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };
  
  const paginatedNotifications = notifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="notifications-page">
      <LoadingBar isLoading={isLoading} />
      
      <h1 className="page-title">Notifications</h1>
      
      <Card className="notifications-card">
        <div className="notifications-header">
          <div className="header-with-icon">
            <Bell size={20} className="header-icon" />
            <h2>All Notifications</h2>
          </div>
          {notifications.length > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead}>
              Mark All as Read
            </Button>
          )}
        </div>
        
        <div className="notifications-list">
          {isLoading ? (
            <div className="loading-placeholder">
              Loading notifications...
            </div>
          ) : notifications.length === 0 ? (
            <div className="empty-state">
              <Bell size={48} className="empty-icon" />
              <p>No notifications to display</p>
            </div>
          ) : (
            <>
              {paginatedNotifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${notification.read ? '' : 'unread'}`}
                >
                  <div className="notification-content">
                    <div className="notification-message">
                      {notification.message}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatDate(notification.created_at || new Date().toISOString())}
                      </span>
                      {!notification.read && (
                        <span className="notification-badge">New</span>
                      )}
                    </div>
                  </div>
                  <div className="notification-actions">
                    {!notification.read && (
                      <Button 
                        variant="ghost" 
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="notification-action-btn"
                      >
                        <Check size={16} />
                        <span className="sr-only">Mark as read</span>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              
              {totalPages > 1 && (
                <div className="pagination">
                  <Button
                    variant="outline"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <ChevronLeft size={16} />
                    <span className="sr-only">Previous page</span>
                  </Button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    <ChevronRight size={16} />
                    <span className="sr-only">Next page</span>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default NotificationsPage; 